import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Loader2, Activity, HeartHandshake, CalendarClock } from "lucide-react";
import { appointmentStatus, toneClasses, formatDate } from "@/lib/appointment";

const Block = ({ title, value }) =>
  value ? (
    <div className="rounded-lg border border-gray-200 bg-gray-50 p-5">
      <h3 className="font-heading text-lg font-semibold text-gray-900">{title}</h3>
      <p className="mt-1 text-[15px] leading-relaxed text-gray-800">{value}</p>
    </div>
  ) : null;

export default function PublicSummary() {
  const { caseId } = useParams();
  const [data, setData] = useState(undefined);

  useEffect(() => {
    api.getPublicCase(caseId).then(setData).catch(() => setData(null));
  }, [caseId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (data === undefined) {
    return <div className="flex h-screen items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  }
  if (data === null) {
    return (
      <div className="flex h-screen flex-col items-center justify-center px-6 text-center">
        <p className="font-heading text-xl font-semibold text-foreground">Summary not found</p>
        <p className="mt-1 text-sm text-muted-foreground">This link may be invalid or the summary was removed.</p>
      </div>
    );
  }

  const ps = data.patient_summary || {};
  const hasContent = Object.values(ps).some((v) => (v || "").toString().trim());
  const status = appointmentStatus(data.next_appointment);

  return (
    <div className="min-h-screen bg-background px-6 pb-24 pt-10">
      <div className="mx-auto max-w-2xl">
        <div className="mb-6 flex items-center gap-2 border-b-2 border-primary pb-3">
          <Activity className="h-6 w-6 text-primary" />
          <div>
            <p className="font-heading text-xl font-semibold text-primary">OncoDx Clinic</p>
            <p className="text-xs text-muted-foreground">Your care summary</p>
          </div>
        </div>

        {data.patient?.name && (
          <h1 className="font-heading text-2xl font-semibold text-foreground">
            Hello {data.patient.name.split(" ")[0]} 👋
          </h1>
        )}

        {data.next_appointment && (
          <div className={`mt-4 flex items-center gap-2 rounded-md border px-4 py-3 text-sm font-medium ${status ? toneClasses[status.tone] : ""}`}
            data-testid="public-appointment">
            <CalendarClock className="h-4 w-4" />
            Next follow-up: {formatDate(data.next_appointment)}{status ? ` · ${status.label}` : ""}
          </div>
        )}

        {hasContent ? (
          <div className="mt-6 flex flex-col gap-4" data-testid="public-summary-sheet">
            <Block title="What's going on" value={ps.overview} />
            <Block title="What your tests showed" value={ps.what_tests_showed} />
            <Block title="Your diagnosis" value={ps.your_diagnosis} />
            <Block title="Your treatment plan" value={ps.your_treatment} />
            <Block title="What to watch for" value={ps.what_to_watch} />
            <Block title="Your follow-up plan" value={ps.your_followup} />
          </div>
        ) : (
          <div className="mt-8 flex flex-col items-center gap-3 rounded-lg border border-dashed border-border p-10 text-center">
            <HeartHandshake className="h-10 w-10 text-muted-foreground" strokeWidth={1.5} />
            <p className="text-sm text-muted-foreground">
              Your plain-language summary isn't ready yet. Please check back after your clinician prepares it.
            </p>
          </div>
        )}

        <p className="mt-8 border-t border-border pt-3 text-xs text-muted-foreground">
          Demo / educational tool only — not a certified medical device. Always consult your doctor.
        </p>
      </div>
    </div>
  );
}
